/** biome-ignore-all lint/a11y/useValidAriaRole: suppressed */
"use client";

import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { type ReactNode, useEffect } from "react";
import authBackground from "@/assets/images/auth-background.png";
import authBackgroundDark from "@/assets/images/auth-background-dark.png";
import { useMenu } from "@/hooks/use-menu";
import useMenuConfig from "@/hooks/use-menu-config";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSettings } from "@/providers/settings-provider";
import { Footer } from "./components/footer";
import { Header } from "./components/header";
import { Sidebar } from "./components/sidebar";

export function Layout({ children }: { children: ReactNode }) {
	const isMobile = useIsMobile();
	const menuSidebar = useMenuConfig();
	const pathname = usePathname();
	const { theme } = useTheme();
	const { getCurrentItem } = useMenu(pathname as string);
	const item = getCurrentItem(menuSidebar);
	const { settings, setOption } = useSettings();
	const isAuthPathname = pathname.startsWith("/auth");
	const isPosPathname = pathname.startsWith("/pos");
	// POS renders its own full-screen chrome; hide the dashboard sidebar/header/footer.
	const hideChrome = isAuthPathname || isPosPathname;

	useEffect(() => {
		const bodyClass = document.body.classList;

		if (settings.layouts.sidebarCollapse) {
			bodyClass.add("sidebar-collapse");
		} else {
			bodyClass.remove("sidebar-collapse");
		}
	}, [settings]); // Runs only on settings update

	useEffect(() => {
		if (pathname.startsWith("/auth") || pathname.startsWith("/pos")) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [pathname]);

	useEffect(() => {
		// Set current layout
		setOption("layout", "pos-dashboard");
	}, [setOption]);

	useEffect(() => {
		const bodyClass = document.body.classList;

		// Add a class to the body element
		bodyClass.add("pos-dashboard");
		bodyClass.add("sidebar-fixed");
		bodyClass.add("header-fixed");

		const timer = setTimeout(() => {
			bodyClass.add("layout-initialized");
		}, 1000); // 1000 milliseconds

		// Remove the class when the component is unmounted
		return () => {
			bodyClass.remove("pos-dashboard");
			bodyClass.remove("sidebar-fixed");
			bodyClass.remove("sidebar-collapse");
			bodyClass.remove("header-fixed");
			bodyClass.remove("layout-initialized");
			clearTimeout(timer);
		};
	}, []); // Runs only once on mount

	return (
		<>
			<title>{item?.title}</title>

			{!isMobile && !hideChrome && <Sidebar />}

			<div
				className={`${hideChrome ? "" : "wrapper"} flex flex-col min-h-screen`}
				style={{
					backgroundImage: isAuthPathname
						? `url("${theme === "dark" ? authBackgroundDark.src : authBackground.src}")`
						: "",
					backgroundSize: isAuthPathname ? "cover" : undefined,
					backgroundRepeat: isAuthPathname ? "no-repeat" : undefined,
					backgroundPosition: isAuthPathname ? "center" : undefined,
				}}
			>
				{!hideChrome && <Header />}

				<main
					className={isPosPathname ? "flex-1" : "flex-1 pt-5"}
					role="content"
				>
					{children}
				</main>

				{!hideChrome && <Footer />}
			</div>
		</>
	);
}
